import React from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import Spinner from 'react-spinkit'
import { Form, Field } from 'react-final-form'
import { useHistory } from 'react-router-dom'
import styles from './index.module.scss'
import NormalLayoutContainer from '../shared/NormalLayoutContainer.js'
import PageTitle from '../../components/page-title/index.js'
import QUERY_KEYS from '../../graphql/queryKeys.js'
import useQuery from '../../hooks/useQuery.js'
import { CheckboxInput } from '../../components/CheckboxInput.js'
import onlineScheduleCustomizationsQuery from '../../graphql/queries/onlineScheduleCustomizations.js'
import useMutation from '../../hooks/useMutation.js'
import updateOnlineScheduleCustomizationsMutations from '../../graphql/mutations/updateOnlineScheduleCustomizations.js'
import notify from '../../utilities/notify.js'
import { captureErrorAndNotify } from '../../utilities/errorHandlers.js'
import { ROUTES } from '../../routes.js'

export default function OnlineScheduleAgreementsPage () {
  const history = useHistory()
  const { user } = useSelector(({ user }) => ({ user: user.user }))

  const { data: { onlineScheduleCustomizations: customizations }, isFetching: customizationsFetching } = useQuery(
    [QUERY_KEYS.onlineScheduleCustomizations, { haulerId: user.haulerId }],
    onlineScheduleCustomizationsQuery,
    {
      enabled: true,
      onError (err) {
        captureErrorAndNotify(err)
      },
      placeholderData: { defaults: {}, savedResults: {} }
    })

  const { mutateAsync: updateOnlineScheduleCustomizations, isLoading: isUpdating } = useMutation(updateOnlineScheduleCustomizationsMutations, {
    async onSuccess () {
      notify('success', 'Settings have been updated')
      history.push(ROUTES.onlineSchedule)
    },
    onError (error) {
      captureErrorAndNotify(error, 'There was an error saving the settings')
    }
  })

  function onSubmit (values) {
    updateOnlineScheduleCustomizations({
      haulerId: user.haulerId,
      settings: {
        haulerAgreement: values.acceptAgreement
      }
    })
  }

  function validate (values) {
    const errors = {}

    // TODO: add back when subdomain stuff is 100% ready for real
    // if (!values.subdomain) {
    //   errors.subdomain = 'Required'
    // }
    // if (/^(?!-)(?!.*--)[a-zA-Z0-9-]{1,63}(?<!-)$/.test(values.subdomain) === false) {
    //   errors.subdomain = 'Invalid format'
    // }

    if (!values.acceptAgreement) {
      errors.acceptAgreement = 'Must accept agreement before proceeding'
    }

    return errors
  }

  if (customizationsFetching) {
    return (
      <Spinner name='circle' fadeIn='none' />
    )
  }

  const initialValues = {
    subdomain: customizations.savedResults?.subdomain,
    acceptAgreement: Boolean(customizations.savedResults?.haulerAgreement)
  }

  return (
    <NormalLayoutContainer showBackLink>
      <PageTitle>
        Online Schedule: Finalize Process (Step: 3/3)
      </PageTitle>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        className={cn(styles.agreementsForm)}
        validate={values => validate(values)}>
        {({ form, handleSubmit, invalid, pristine }) => {
          return (
            <form autoComplete='off' onSubmit={handleSubmit}>
              <div className={cn('dis-panel')}>
                <div className='dis-panel-header'>
                  <h5>Online Schedule Domain</h5>
                </div>
                <div className={cn('dis-panel-body')}>
                  <p>
                    Need to set up or change your domain? Our team is here to help.
                    Contact us at <a href='tel:855-757-6400'>855-757-6400</a>, <a href='mailto:support@dispatcher.com&subject=Setup%20Online%20Schedule%20Domain'>support@dispatcher.com</a>,
                    or through the chat option. Your request can take up to 2 business days to resolve.
                  </p>

                  {customizations.savedResults?.subdomain && (
                    <div className={cn(styles.subdomain)}>
                      <a
                        href={`https://${customizations.savedResults?.subdomain}.dispatcher.com/store`}
                        className={cn('dis-btn dis-btn-sm dis-btn-primary')}
                        target='_blank' rel='noreferrer'>
                        {customizations.savedResults?.subdomain}.dispatcher.com/store
                        <span className={cn('material-icons-outlined pl-2')}>
                          link
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className={cn('dis-panel')}>
                <div className='dis-panel-header'>
                  <h5>User Agreement and Privacy Policy</h5>
                </div>
                <div className={cn('dis-panel-body')}>
                  <div className={cn(styles.userAgreement)}>
                    <p>
                      Please refer to the Dispatcher Privacy Policy for specific details.
                    </p>
                    <a
                      href='https://www.dispatcher.com/privacy-policy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className={cn('dis-btn dis-btn-lg dis-btn-success', styles.downloadButton)}>
                      View User Agreement
                      <i className='material-icons dis-btn-icon'>file_download</i>
                    </a>
                  </div>
                  <Field
                    type='checkbox'
                    name='acceptAgreement'
                    label='I agree to the Dispatcher Privacy Policy as it pertains to Online Schedule.'
                    component={CheckboxInput}
                  />
                  <div className={cn(styles.buttonWrapper)}>
                    <button
                      type='submit'
                      className={cn('dis-btn dis-btn-primary dis-btn-lg')}
                      disabled={pristine || invalid || isUpdating}>
                      <span>Save Changes</span>
                      <i className='material-icons'>check</i>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )
        }}
      </Form>
    </NormalLayoutContainer>
  )
}
